import { Button } from '@my/ui';
import { useQueryClient } from '@tanstack/react-query';
import { CircleCheck, CircleX, Loader2, MinusCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { commitDraft, runPool } from '../lib/commitImport';
import { isCommittable, validateAll } from '../lib/validateImportRows';
import type { DraftStatus, ImportDraft } from '../lib/types';

interface CommitStepProps {
  drafts: ImportDraft[];
  excludedRefs: Set<string>;
  organisationId: string;
  onDraftChange: (rowRef: string, patch: Partial<ImportDraft>) => void;
  onBack: () => void;
  onDone: () => void;
}

/**
 * Step 4 — commit. Imports the ready rows by reusing the create endpoints
 * per-row (address → party) with bounded concurrency, writing each outcome
 * back live. Failed rows can be retried in place. A result table shows
 * created / skipped / failed per row.
 */
export function CommitStep({
  drafts,
  excludedRefs,
  organisationId,
  onDraftChange,
  onBack,
  onDone,
}: CommitStepProps) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<'idle' | 'running' | 'done'>('idle');
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // The rows we'll import: included + no blocking errors.
  const committable = useMemo(() => {
    const issues = validateAll(drafts);
    return drafts.filter((d) => !excludedRefs.has(d.rowRef) && isCommittable(issues.get(d.rowRef)));
  }, [drafts, excludedRefs]);

  const counts = useMemo(() => {
    let created = 0;
    let skipped = 0;
    let failed = 0;
    for (const d of committable) {
      if (d.status_ === 'created') created += 1;
      else if (d.status_ === 'skipped') skipped += 1;
      else if (d.status_ === 'failed') failed += 1;
    }
    return { created, skipped, failed };
  }, [committable]);

  const run = async (list: ImportDraft[]) => {
    if (list.length === 0) return;
    setPhase('running');
    setProgress({ done: 0, total: list.length });
    await runPool(
      list,
      async (d) => {
        onDraftChange(d.rowRef, { status_: 'creating', error: undefined });
        const updated = await commitDraft(organisationId, d);
        onDraftChange(d.rowRef, {
          status_: updated.status_,
          assignedCode: updated.assignedCode,
          createdPartyId: updated.createdPartyId,
          createdPrimaryAddressId: updated.createdPrimaryAddressId,
          createdBillingAddressId: updated.createdBillingAddressId,
          error: updated.error,
        });
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      },
      5,
    );
    // Refresh the party list caches so the new rows show on Customers/Suppliers.
    qc.invalidateQueries({ queryKey: ['directory', 'parties', 'org', organisationId] });
    setPhase('done');
  };

  const startList = committable.filter((d) => d.status_ !== 'created');
  const failedRows = committable.filter((d) => d.status_ === 'failed');

  return (
    <div className="space-y-4">
      {/* Summary / progress */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {phase === 'running' ? (
          <span className="inline-flex items-center gap-2 text-slate-700">
            <Loader2 className="h-4 w-4 animate-spin" />
            Importing {progress.done}/{progress.total}…
          </span>
        ) : (
          <span className="text-slate-600">
            {committable.length} row{committable.length === 1 ? '' : 's'} ready to import
            {phase === 'done' && ' · done'}
          </span>
        )}
        {(counts.created > 0 || phase === 'done') && (
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <CircleCheck className="h-4 w-4" /> {counts.created} created
          </span>
        )}
        {counts.skipped > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-600">
            <MinusCircle className="h-4 w-4" /> {counts.skipped} skipped
          </span>
        )}
        {counts.failed > 0 && (
          <span className="inline-flex items-center gap-1 text-red-600">
            <CircleX className="h-4 w-4" /> {counts.failed} failed
          </span>
        )}
      </div>

      {/* Result table */}
      <div className="max-h-[28rem] overflow-auto rounded-md border border-slate-200">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="w-8 px-2 py-1.5" />
              <th className="px-2 py-1.5 font-semibold">Name</th>
              <th className="px-2 py-1.5 font-semibold">Code</th>
              <th className="px-2 py-1.5 font-semibold">Result</th>
            </tr>
          </thead>
          <tbody>
            {committable.map((d) => (
              <tr key={d.rowRef} className="border-t border-slate-100">
                <td className="px-2 py-1">
                  <StatusIcon status={d.status_} />
                </td>
                <td className="px-2 py-1 text-slate-800">{d.name || <em className="text-slate-400">—</em>}</td>
                <td className="px-2 py-1 font-mono text-slate-600">
                  {d.assignedCode || d.code || <span className="text-slate-400">(auto)</span>}
                </td>
                <td className="px-2 py-1 text-slate-500">{d.error ?? labelFor(d.status_)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-3">
        <Button type="button" variant="ghost" onClick={onBack} disabled={phase === 'running'}>
          Back
        </Button>
        <div className="flex items-center gap-2">
          {failedRows.length > 0 && phase !== 'running' && (
            <Button type="button" variant="secondary" onClick={() => void run(failedRows)}>
              Retry failed ({failedRows.length})
            </Button>
          )}
          {phase === 'done' ? (
            <Button type="button" onClick={onDone}>
              Done
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => void run(startList)}
              disabled={phase === 'running' || startList.length === 0}
            >
              Import {startList.length} row{startList.length === 1 ? '' : 's'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function labelFor(status: DraftStatus): string {
  switch (status) {
    case 'created':
      return 'Created';
    case 'skipped':
      return 'Skipped';
    case 'creating':
      return 'Creating…';
    case 'failed':
      return 'Failed';
    default:
      return 'Pending';
  }
}

function StatusIcon({ status }: { status: DraftStatus }) {
  switch (status) {
    case 'created':
      return <CircleCheck className="h-4 w-4 text-emerald-500" />;
    case 'skipped':
      return <MinusCircle className="h-4 w-4 text-amber-500" />;
    case 'failed':
      return <CircleX className="h-4 w-4 text-red-500" />;
    case 'creating':
      return <Loader2 className="h-4 w-4 animate-spin text-slate-400" />;
    default:
      return <span className="ml-1 inline-block h-2 w-2 rounded-full bg-slate-300" />;
  }
}
